import { RefObject, useCallback, RefCallback, useRef } from "react";

/**
 * Merge multiple refs into a callback ref that can be passed to `ref`.
 *
 * This React hook merges multiple refs into a single callback function.
 * It accepts an arbitrary number of arguments, each of which can be null,
 * undefined, a mutable ref object, or a ref callback function. It returns
 * a callback function that can be passed to a React component's ref
 * attribute.
 *
 * Example Usage:
 * ```
 * const mergedRef = useMergedRefs(ref1, ref2, ref3);
 * <div ref={mergedRef}></div>
 * ```
 */
export const useMergedRefs = <T>(
  ...args: Array<
    | null
    | undefined
    | RefObject<T | null>
    | RefCallback<T>
    | ((value: T | null, ...otherArgs: any[]) => void)
  >
): RefCallback<T> | ((value: T | null, ...otherArgs: any[]) => void) => {
  const mostRecentRef = useRef<T | null>(null);
  const mostRecentArgs = useRef<any[]>([]);
  const refArray = useRef<null | Array<
    | null
    | undefined
    | RefObject<T | null>
    | RefCallback<T>
    | ((value: T | null, ...otherArgs: any[]) => void)
  >>(null);
  if (refArray.current && mostRecentRef.current) {
    for (const ref of args) {
      if (ref && !refArray.current.includes(ref)) {
        if (typeof ref === "function") {
          ref(mostRecentRef.current, ...mostRecentArgs.current);
        } else {
          ref.current = mostRecentRef.current;
        }
      }
    }
    for (const ref of refArray.current) {
      if (ref && !args.includes(ref)) {
        if (typeof ref === "function") {
          ref(null);
        } else {
          ref.current = null;
        }
      }
    }
  }

  refArray.current = args;
  return useCallback((value: T, ...otherArgs: any[]) => {
    mostRecentRef.current = value;
    mostRecentArgs.current = otherArgs;
    if (refArray.current) {
      for (const ref of refArray.current) {
        if (ref) {
          if (typeof ref === "function") {
            ref(value, ...otherArgs);
          } else {
            ref.current = value;
          }
        }
      }
    }
  }, []);
};
